import { FormEvent, useState } from 'react';
import { ArrowRight, Check, LoaderCircle } from 'lucide-react';
import { AtmosphericLayer } from './AtmosphericLayer';

type FormStatus = { state: 'idle' | 'submitting' | 'success' | 'error'; message?: string };

export function Contact() {
  const [status, setStatus] = useState<FormStatus>({ state: 'idle' });

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setStatus({ state: 'submitting' });

    try {
      const response = await fetch('/api/inquiries', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name: data.get('name'), email: data.get('email'), company: data.get('company'),
          problem: data.get('problem'), website: data.get('website'), sourcePath: window.location.pathname,
          referrer: document.referrer,
        }),
      });
      const result = await response.json() as { ok?: boolean; error?: string };
      if (!response.ok || !result.ok) throw new Error(result.error || 'The project note could not be saved.');
      form.reset();
      setStatus({ state: 'success', message: 'Project note received. I’ll follow up using the details you provided.' });
    } catch (error) {
      setStatus({ state: 'error', message: error instanceof Error ? error.message : 'The project note could not be saved.' });
    }
  }

  return (
    <section className="contact-stage" id="contact">
      <AtmosphericLayer plate="output" placement="contact" />
      <div className="contact-stage__inner">
      <div className="contact section-shell">
      <div className="contact__copy reveal"><span className="section-kicker">Start a project</span><h2>Show me what should run better.</h2><p>Tell me about the repetitive work, disconnected tools, or software that almost fits. I’ll map the practical opportunity and tell you what kind of system would help.</p><small>No generic software pitch. We start with how the work actually moves.</small><a className="contact-email" href="mailto:hello@workcrafted.com">hello@workcrafted.com</a></div>
      <form className="inquiry-form reveal" onSubmit={onSubmit} noValidate>
        <div className="inquiry-form__row"><label><span>Your name</span><input name="name" autoComplete="name" placeholder="Your full name" required minLength={2} maxLength={80} /></label>
        <label><span>Work email</span><input name="email" type="email" autoComplete="email" placeholder="you@company.com" required maxLength={254} /></label></div>
        <label><span>Company <em>(optional)</em></span><input name="company" autoComplete="organization" placeholder="Company name" maxLength={120} /></label>
        <label className="inquiry-form__problem"><span>What isn't working?</span><textarea name="problem" placeholder="The repetitive work, the tools that do not talk to each other, or the software that almost fits." required minLength={20} maxLength={4000} rows={5} /></label>
        <label className="form-honeypot" aria-hidden="true"><span>Website</span><input name="website" tabIndex={-1} autoComplete="off" /></label>
        <button type="submit" disabled={status.state === 'submitting'}>
          <span>{status.state === 'submitting' ? 'Saving your note' : 'Start a project'}</span>
          {status.state === 'submitting' ? <LoaderCircle className="spin" aria-hidden="true" /> : <ArrowRight aria-hidden="true" />}
        </button>
        <p className="form-privacy">Your details are used only to respond to this inquiry.</p>
        <div className={`form-status form-status--${status.state}`} role="status" aria-live="polite">
          {status.state === 'success' && <Check aria-hidden="true" />}
          {status.message && <span>{status.message}</span>}
        </div>
      </form>
      </div>
      </div>
    </section>
  );
}
